/**
 * SuperAdmin — TB-elimination Oath / Pledge analytics.
 *
 * Shows who has taken the pledge (captured right after the inauguration
 * curtain) plus all analytics + search options: headline metric cards, a
 * by-state breakdown, a 30-day trend, top districts, a searchable/filterable
 * paginated table (by name/email/mobile, state, district, date range), and a
 * CSV export. Charts use plain CSS bars (no recharts dependency) matching the
 * MiniChart style already used on the SuperAdmin dashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/stores/authStore'
import GovLayout from '@/components/layout/GovLayout'
import {
  ScrollText, Users, CalendarDays, CalendarRange, Mail, MapPin, Search,
  Download, RefreshCw, ChevronLeft, ChevronRight, TrendingUp, Loader2, Languages, FileDown,
} from 'lucide-react'
import { jsPDF } from 'jspdf'
import { INDIAN_STATES, getDistricts } from '@/data/indianStates'
import {
  getOathStats, searchOaths, exportOaths,
  type OathStats, type OathRecord, type OathSearchParams,
} from '@/services/oathService'

/** Pledge-language code -> human label (matches the oath toggle). */
const LANG_LABELS: Record<string, string> = {
  en: 'English',
  hi: 'हिंदी',
  mr: 'मराठी',
  unknown: 'Unknown',
}
const langLabel = (code?: string | null) =>
  (code && LANG_LABELS[code]) || (code ? code : '—')

/** English-only labels for the PDF (jsPDF standard fonts have no Devanagari glyphs). */
const LANG_LABELS_PDF: Record<string, string> = {
  en: 'English', hi: 'Hindi', mr: 'Marathi', unknown: 'Unknown',
}
const langLabelPdf = (code?: string | null) =>
  (code && LANG_LABELS_PDF[code]) || (code ? code : 'Unknown')

// ---------------- small presentational helpers ----------------

const MetricCard = ({ icon: Icon, label, value, color }: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: number | string
  color: 'blue' | 'green' | 'purple' | 'orange' | 'cyan'
}) => {
  const gradients = {
    blue: 'from-blue-500 to-cyan-400',
    green: 'from-green-500 to-emerald-400',
    purple: 'from-purple-500 to-pink-400',
    orange: 'from-orange-500 to-amber-400',
    cyan: 'from-cyan-500 to-blue-400',
  }
  return (
    <div className="rounded-2xl bg-white shadow-lg p-5">
      <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${gradients[color]} text-white shadow`}>
        <Icon className="w-6 h-6" />
      </div>
      <p className="text-sm font-medium text-gray-500 mt-3">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">
        {typeof value === 'number' ? value.toLocaleString('en-IN') : value}
      </p>
    </div>
  )
}

const Bar = ({ label, value, max, color = 'bg-blue-500' }: {
  label: string
  value: number
  max: number
  color?: string
}) => (
  <div className="flex items-center gap-3 text-sm">
    <span className="w-40 shrink-0 truncate text-gray-700" title={label}>{label}</span>
    <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${max ? Math.max(2, (value / max) * 100) : 0}%` }} />
    </div>
    <span className="w-12 shrink-0 text-right font-medium text-gray-900">{value.toLocaleString('en-IN')}</span>
  </div>
)

// ---------------- page ----------------

const SORT_OPTIONS = [
  { value: 'createdAt', label: 'Newest first' },
  { value: 'name', label: 'Name (A–Z)' },
  { value: 'email', label: 'Email (A–Z)' },
  { value: 'state', label: 'State (A–Z)' },
  { value: 'district', label: 'District (A–Z)' },
]

const OathAnalyticsPage = () => {
  const { user, isAuthenticated } = useAuthStore()
  const navigate = useNavigate()

  const [stats, setStats] = useState<OathStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)

  // filters
  const [search, setSearch] = useState('')
  const [state, setState] = useState('')
  const [district, setDistrict] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [page, setPage] = useState(0)
  const [size, setSize] = useState(10)

  // list
  const [rows, setRows] = useState<OathRecord[]>([])
  const [totalElements, setTotalElements] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [listLoading, setListLoading] = useState(false)
  const [exporting, setExporting] = useState(false)
  const [pdfing, setPdfing] = useState(false)

  const districts = useMemo(() => getDistricts(state), [state])

  const fetchStats = useCallback(async () => {
    setStatsLoading(true)
    try {
      setStats(await getOathStats())
    } catch (e) {
      console.error('oath stats failed', e)
    } finally {
      setStatsLoading(false)
    }
  }, [])

  const fetchList = useCallback(async () => {
    setListLoading(true)
    try {
      const params: OathSearchParams = {
        search: search.trim() || undefined,
        state: state || undefined,
        district: district || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        sortBy,
        page,
        size,
      }
      const res = await searchOaths(params)
      setRows(res.content)
      setTotalElements(res.totalElements)
      setTotalPages(res.totalPages)
    } catch (e) {
      console.error('oath list failed', e)
      setRows([])
      setTotalElements(0)
      setTotalPages(0)
    } finally {
      setListLoading(false)
    }
  }, [search, state, district, dateFrom, dateTo, sortBy, page, size])

  useEffect(() => {
    if (!isAuthenticated) { navigate('/login'); return }
    if (user && user.role !== 'SUPER_ADMIN' && user.role !== 'ADMIN') { navigate('/feed'); return }
    fetchStats()
  }, [user, isAuthenticated, navigate, fetchStats])

  useEffect(() => {
    if (user && (user.role === 'SUPER_ADMIN' || user.role === 'ADMIN')) {
      fetchList()
    }
  }, [user, fetchList])

  // Reset to first page whenever a filter changes (not on page itself).
  useEffect(() => { setPage(0) }, [search, state, district, dateFrom, dateTo, sortBy, size])

  function applyFilters() {
    setPage(0)
    fetchList()
  }

  async function handleExport() {
    setExporting(true)
    try {
      await exportOaths({
        search: search.trim() || undefined,
        state: state || undefined,
        district: district || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
      })
    } catch (e) {
      console.error('oath export failed', e)
    } finally {
      setExporting(false)
    }
  }

  /** Generate a multi-page PDF report of the current filtered pledge set.
   *  Uses jsPDF text primitives (no html2canvas) so it paginates cleanly and
   *  stays light. Language labels are romanized for the PDF because jsPDF's
   *  standard fonts have no Devanagari glyphs. */
  async function handleDownloadPdf() {
    setPdfing(true)
    try {
      const params: OathSearchParams = {
        search: search.trim() || undefined,
        state: state || undefined,
        district: district || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo || undefined,
        sortBy,
        page: 0,
        size: 200,
      }
      const res = await searchOaths(params)
      const allRows = res.content
      const s = stats
      const doc = new jsPDF({ unit: 'pt', format: 'a4' })
      const pageH = doc.internal.pageSize.getHeight()
      const margin = 40
      let y = margin

      const ensure = (need: number) => {
        if (y + need > pageH - margin) { doc.addPage(); y = margin }
      }
      const line = (text: string, size = 10, bold = false) => {
        ensure(size + 4)
        doc.setFont('helvetica', bold ? 'bold' : 'normal')
        doc.setFontSize(size)
        doc.text(text, margin, y)
        y += size + 4
      }

      // Title
      line('TB Mukt Bharat — Oath / Pledge Report', 16, true)
      line(`Generated: ${new Date().toLocaleString('en-IN')}`, 9)
      y += 4
      // Applied filters summary
      const filters: string[] = []
      if (search.trim()) filters.push(`search="${search.trim()}"`)
      if (state) filters.push(`state=${state}`)
      if (district) filters.push(`district=${district}`)
      if (dateFrom) filters.push(`from=${dateFrom}`)
      if (dateTo) filters.push(`to=${dateTo}`)
      line(filters.length ? `Filters: ${filters.join(', ')}` : 'Filters: none (all pledges)', 9)
      y += 6

      // Headline metrics
      line('Headline metrics', 12, true)
      if (s) {
        line(`Total pledges: ${s.totalOaths}   |   Today: ${s.today}   |   This week: ${s.thisWeek}   |   Unique emails: ${s.uniqueEmails}`)
        line(`Top state: ${s.topState || '—'}`)
      }
      y += 4

      // Breakdowns
      if (s) {
        line('Pledges by state', 11, true)
        Object.entries(s.byState).forEach(([st, c]) => line(`   ${st}: ${c}`))
        y += 4
        if (s.byLanguage) {
          line('Pledge language', 11, true)
          Object.entries(s.byLanguage).forEach(([code, c]) => line(`   ${langLabelPdf(code)}: ${c}`))
          y += 4
        }
        if (s.topDistricts?.length) {
          line('Top districts', 11, true)
          s.topDistricts.slice(0, 10).forEach((d) => line(`   ${d.district}: ${d.count}`))
          y += 4
        }
      }

      // Table
      line('Pledge-takers', 12, true)
      const cols = ['Name', 'Email', 'Mobile', 'State', 'District', 'Lang', 'Date']
      const widths = [90, 130, 60, 60, 90, 50, 60]
      const startX = margin
      const rowH = 16
      const drawHeader = () => {
        ensure(rowH + 4)
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(8)
        doc.setFillColor(240, 240, 240)
        doc.rect(startX - 2, y - 10, widths.reduce((a, b) => a + b, 0) + 4, rowH, 'F')
        let x = startX
        cols.forEach((c, i) => { doc.text(c, x, y); x += widths[i] })
        y += rowH + 2
      }
      drawHeader()
      doc.setFont('helvetica', 'normal')
      doc.setFontSize(8)
      allRows.forEach((r) => {
        ensure(rowH + 2)
        const dt = r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
        const cells = [
          (r.fullName || '').slice(0, 22),
          (r.email || '').slice(0, 28),
          r.mobile || '',
          (r.state || '').slice(0, 12),
          (r.district || '').slice(0, 20),
          langLabelPdf(r.language),
          dt,
        ]
        let x = startX
        cells.forEach((cVal, i) => { doc.text(String(cVal), x, y); x += widths[i] })
        y += rowH
      })
      if (res.totalElements > allRows.length) {
        y += 4
        line(`(Showing first ${allRows.length} of ${res.totalElements} — use Export CSV for the full set.)`, 8)
      }

      doc.save('oaths-report.pdf')
    } catch (e) {
      console.error('oath pdf failed', e)
    } finally {
      setPdfing(false)
    }
  }

  const topStateCount = stats ? Math.max(1, ...Object.values(stats.byState)) : 1
  const top10States = stats ? Object.entries(stats.byState).slice(0, 10) : []
  const topTrend = stats?.trend ?? []
  const trendMax = Math.max(1, ...topTrend.map((d) => d.count))
  const topDistricts = stats?.topDistricts?.slice(0, 10) ?? []
  const districtMax = Math.max(1, ...topDistricts.map((d) => d.count))
  const byLanguage = stats?.byLanguage ? Object.entries(stats.byLanguage) : []
  const langMax = Math.max(1, ...byLanguage.map(([, c]) => c))

  const inputCls = 'w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100'
  const labelCls = 'block text-xs font-medium text-gray-600 mb-1'

  return (
    <GovLayout>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <ScrollText className="w-7 h-7 text-blue-600" />
              Oath / Pledge Analytics
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Visitors who took the TB-elimination pledge after the inauguration curtain.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => { fetchStats(); fetchList() }}
              className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
            <button
              onClick={handleDownloadPdf}
              disabled={pdfing || totalElements === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-2 text-sm font-medium text-white hover:bg-purple-700 disabled:opacity-60"
            >
              {pdfing ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              Download PDF
            </button>
            <button
              onClick={handleExport}
              disabled={exporting || totalElements === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
          </div>
        </div>

        {/* Metric cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-8">
          <MetricCard icon={ScrollText} label="Total Pledges" value={stats?.totalOaths ?? 0} color="blue" />
          <MetricCard icon={CalendarDays} label="Today" value={stats?.today ?? 0} color="green" />
          <MetricCard icon={CalendarRange} label="This Week" value={stats?.thisWeek ?? 0} color="cyan" />
          <MetricCard icon={Mail} label="Unique Emails" value={stats?.uniqueEmails ?? 0} color="purple" />
          <MetricCard icon={MapPin} label="Top State" value={stats?.topState || '—'} color="orange" />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* By state */}
          <div className="bg-white rounded-2xl shadow-lg p-6 lg:col-span-1">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-600" /> Pledges by State (top 10)
            </h3>
            {statsLoading ? (
              <div className="h-40 flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : top10States.length === 0 ? (
              <p className="text-sm text-gray-400">No data yet.</p>
            ) : (
              <div className="space-y-2.5">
                {top10States.map(([s, c]) => (
                  <Bar key={s} label={s} value={c} max={topStateCount} />
                ))}
              </div>
            )}
          </div>

          {/* Trend */}
          <div className="bg-white rounded-2xl shadow-lg p-6 lg:col-span-2">
            <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-600" /> Pledges — last 30 days
            </h3>
            {statsLoading ? (
              <div className="h-40 flex items-center justify-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
              <div className="flex items-end gap-1 h-40">
                {topTrend.map((d) => (
                  <div key={d.date} className="flex-1 group relative">
                    <div
                      className="w-full rounded-t bg-gradient-to-t from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 transition"
                      style={{ height: `${(d.count / trendMax) * 100}%`, minHeight: d.count > 0 ? '4px' : '0' }}
                      title={`${d.date}: ${d.count}`}
                    />
                  </div>
                ))}
              </div>
            )}
            {topTrend.length > 0 && (
              <div className="flex justify-between text-[10px] text-gray-400 mt-2">
                <span>{topTrend[0].date}</span>
                <span>{topTrend[topTrend.length - 1].date}</span>
              </div>
            )}
          </div>
        </div>

        {/* Top districts */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4">Top Districts</h3>
          {topDistricts.length === 0 ? (
            <p className="text-sm text-gray-400">No data yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
              {topDistricts.map((d) => (
                <Bar key={d.district} label={d.district} value={d.count} max={districtMax} color="bg-emerald-500" />
              ))}
            </div>
          )}
        </div>

        {/* Pledge language breakdown */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
          <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Languages className="w-5 h-5 text-purple-600" /> Pledge Language
          </h3>
          {byLanguage.length === 0 ? (
            <p className="text-sm text-gray-400">No data yet.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-2.5">
              {byLanguage.map(([code, c]) => (
                <Bar key={code} label={langLabel(code)} value={c} max={langMax} color="bg-purple-500" />
              ))}
            </div>
          )}
        </div>

        {/* Filters + table */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="font-bold text-gray-900 mb-4">Pledge-takers</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <div className="lg:col-span-2">
              <label className={labelCls}>Search (name / email / mobile)</label>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
                  className={inputCls + ' pl-9'}
                  placeholder="Search…"
                />
              </div>
            </div>
            <div>
              <label className={labelCls}>State</label>
              <select value={state} onChange={(e) => { setState(e.target.value); setDistrict('') }} className={inputCls}>
                <option value="">All states</option>
                {INDIAN_STATES.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>District</label>
              <select value={district} onChange={(e) => setDistrict(e.target.value)} className={inputCls} disabled={!state}>
                <option value="">{state ? 'All districts' : 'Select state'}</option>
                {districts.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>From date</label>
              <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>To date</label>
              <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Sort by</label>
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className={inputCls}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelCls}>Rows per page</label>
              <select value={size} onChange={(e) => setSize(Number(e.target.value))} className={inputCls}>
                {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-gray-500 border-b border-gray-200">
                  <th className="py-2 pr-4">Name</th>
                  <th className="py-2 pr-4">Email</th>
                  <th className="py-2 pr-4">Mobile</th>
                  <th className="py-2 pr-4">State</th>
                  <th className="py-2 pr-4">District</th>
                  <th className="py-2 pr-4">Address</th>
                  <th className="py-2 pr-4">Language</th>
                  <th className="py-2 pr-2 whitespace-nowrap">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {listLoading ? (
                  <tr><td colSpan={8} className="py-10 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></td></tr>
                ) : rows.length === 0 ? (
                  <tr><td colSpan={8} className="py-10 text-center text-gray-400">No pledge records.</td></tr>
                ) : rows.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="py-2 pr-4 font-medium text-gray-900">{r.fullName}</td>
                    <td className="py-2 pr-4 text-gray-700">{r.email}</td>
                    <td className="py-2 pr-4 text-gray-700">{r.mobile}</td>
                    <td className="py-2 pr-4 text-gray-700">{r.state}</td>
                    <td className="py-2 pr-4 text-gray-700">{r.district}</td>
                    <td className="py-2 pr-4 text-gray-500 max-w-[220px] truncate" title={r.address || ''}>{r.address || '—'}</td>
                    <td className="py-2 pr-4 whitespace-nowrap text-gray-700">{langLabel(r.language)}</td>
                    <td className="py-2 pr-2 whitespace-nowrap text-gray-500">
                      {r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>
              {totalElements > 0
                ? `Showing ${rows.length} of ${totalElements.toLocaleString('en-IN')} pledges`
                : 'No pledges'}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0 || listLoading}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 disabled:opacity-50 hover:bg-gray-50"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="px-2">Page {page + 1} of {Math.max(1, totalPages)}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                disabled={page >= totalPages - 1 || listLoading}
                className="inline-flex items-center gap-1 rounded-lg border border-gray-300 px-2.5 py-1.5 disabled:opacity-50 hover:bg-gray-50"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </main>
    </GovLayout>
  )
}

export default React.memo(OathAnalyticsPage)